import type { ApiResponse } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import type {
  CreateProjectRequest,
  CreateProjectResponse,
  GetProjectResponse,
  ListMessagesResponse,
  ListProjectResponse,
} from "../../../schemas/codegen";
import { api } from "../api/api";

const PROJECTS_KEY = "PROJECTS";
const PROFILE_KEY = "PROFILE";

export const getProfileQueryOptions = () => ({
  queryKey: [PROFILE_KEY],
  queryFn: () => api.getProfile(),
});

export const listProjectsQueryOptions = () => ({
  queryKey: [PROJECTS_KEY],
  queryFn: () => api.listProjects(),
  select: (response: ApiResponse<ListProjectResponse>) => response.data,
});

export const getProjectQueryOptions = (projectId: string) => ({
  queryKey: [PROJECTS_KEY, projectId],
  queryFn: () => api.getProject(projectId),
  select: (response: ApiResponse<GetProjectResponse>) => response.data,
  enabled: !!projectId,
});

export const listMessagesQueryOptions = (projectId: string) => ({
  queryKey: [PROJECTS_KEY, projectId, "messages"],
  queryFn: () => api.listMessages(projectId),
  select: (response: ApiResponse<ListMessagesResponse>) => response.data,
  enabled: !!projectId,
});

export function useCreateProject() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: (data: CreateProjectRequest) => api.createProject(data),
    onSuccess: async (response: ApiResponse<CreateProjectResponse>) => {
      // Prefetch the project data before navigation
      await queryClient.prefetchQuery(
        getProjectQueryOptions(response.data.project.id),
      );

      // Navigate to the new project
      router.navigate({
        to: "/codegen/$projectId",
        params: { projectId: response.data.project.id },
      });

      // Invalidate the project list in the background
      queryClient.invalidateQueries({ queryKey: [PROJECTS_KEY] });
    },
  });
}
